<script setup lang="ts">
import { ref, watch, computed } from "vue";
import type { SignatureTemplate } from "@/shared/types";
import { templateKey, parseTemplateKey } from "@/features/signature/services/template-cache";
import BaseButton from "@/shared/components/BaseButton.vue";
import SearchSelect from "@/shared/components/SearchSelect.vue";
import type { SearchSelectOption } from "@/shared/components/SearchSelect.vue";

const props = defineProps<{
  templates: readonly SignatureTemplate[];
  newMail?: string;
  reply?: string;
  forward?: string;
  mandatoryTemplateId?: number | null;
}>();

const emit = defineEmits<{
  save: [
    newMail: string,
    reply: string,
    forward: string
  ];
  "preview-signature": [templateId: number];
}>();

const localNewMail = ref(props.newMail || "none");
const localReply = ref(props.reply || "none");
const localForward = ref(props.forward || "none");
const useSameForAll = ref(true);

const templateOptions = computed<SearchSelectOption[]>(() =>
  props.templates.map((t) => ({
    id: templateKey(t.id),
    label: t.name,
  }))
);

const isLocked = computed(() => props.mandatoryTemplateId != null);

watch(
  () => props.newMail,
  (v) => { if (v) localNewMail.value = v; }
);
watch(
  () => props.reply,
  (v) => { if (v) localReply.value = v; }
);
watch(
  () => props.forward,
  (v) => { if (v) localForward.value = v; }
);


function onToggleSame(val: boolean): void {
  useSameForAll.value = val;
  if (val && localNewMail.value !== "none") {
    localReply.value = localNewMail.value;
    localForward.value = localNewMail.value;
  }
}

function onTemplatePicked(option: SearchSelectOption): void {
  const id = parseTemplateKey(option.id);
  if (id !== null) {
    emit("preview-signature", id);
  }
}

function onSameTemplateChange(val: string | number | null): void {
  const key = String(val ?? "none");
  localNewMail.value = key;
  localReply.value = key;
  localForward.value = key;
  const id = parseTemplateKey(key);
  if (id !== null) emit("preview-signature", id);
}

function handleSave(): void {
  emit("save", localNewMail.value, localReply.value, localForward.value);
}
</script>

<template>
  <section class="settings">
    <h3 class="settings__heading">
      Quando aplicar
      <v-icon v-if="isLocked" name="md-lock-outlined" class="lock-icon" aria-hidden="true" />
    </h3>
    <p class="settings__desc">
      {{ isLocked
        ? "Sua organização definiu a assinatura obrigatória para todas as mensagens."
        : "Escolha uma assinatura para cada tipo de mensagem, ou use a mesma em todas." }}
    </p>

    <label v-if="!isLocked" class="settings__toggle">
      <input
        type="checkbox"
        :checked="useSameForAll"
        @change="onToggleSame(($event.target as HTMLInputElement).checked)"
      />
      <span class="settings__toggle-label">Usar a mesma assinatura para tudo</span>
    </label>

    <div v-if="isLocked || useSameForAll" class="settings__unified">
      <SearchSelect
        v-model="localNewMail"
        placeholder="Nenhuma"
        :options="templateOptions"
        :disabled="isLocked"
        @update:model-value="onSameTemplateChange"
        @option-selected="onTemplatePicked"
      />
      <p class="settings__note">
        Aplicada a e-mails novos, respostas e encaminhamentos.
      </p>
    </div>

    <div v-else-if="!isLocked" class="settings__individual">
      <div class="form-group">
        <label class="form-label" for="new_mail">Nova Mensagem</label>
        <SearchSelect
          id="new_mail"
          v-model="localNewMail"
          placeholder="Nenhuma"
          :options="templateOptions"
          @option-selected="onTemplatePicked"
        />
      </div>

      <div class="form-group">
        <label class="form-label" for="reply">Responder</label>
        <SearchSelect
          id="reply"
          v-model="localReply"
          placeholder="Nenhuma"
          :options="templateOptions"
          @option-selected="onTemplatePicked"
        />
      </div>

      <div class="form-group">
        <label class="form-label" for="forward">Encaminhar</label>
        <SearchSelect
          id="forward"
          v-model="localForward"
          placeholder="Nenhuma"
          :options="templateOptions"
          @option-selected="onTemplatePicked"
        />
      </div>
    </div>

    <div v-if="!isLocked" class="settings__actions">
      <BaseButton size="md" block @click="handleSave">
        Salvar configurações
      </BaseButton>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.settings {
  margin-bottom: 0;
}

.settings__heading {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--colorText);
  margin: 0 0 var(--spacing-xs) 0;
}

.lock-icon {
  width: 14px;
  height: 14px;
  color: var(--colorTextTertiary);
  flex-shrink: 0;
}

.settings__desc {
  font-size: var(--font-size-sm);
  color: var(--colorTextSecondary);
  margin: 0 0 var(--spacing-md) 0;
  line-height: 1.5;
}

.settings__toggle {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  cursor: pointer;
  padding: var(--spacing-sm) 0;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: var(--colorPrimary);
    margin-top: 1px;
    flex-shrink: 0;
  }
}

.settings__toggle-label {
  font-size: var(--font-size-md);
  color: var(--colorText);
  line-height: 1.4;
}

.settings__unified {
  margin-top: var(--spacing-sm);
  padding-left: 26px;
}

.settings__note {
  font-size: var(--font-size-sm);
  color: var(--colorTextTertiary);
  margin: var(--spacing-xs) 0 0 0;
  line-height: 1.4;
}

.settings__individual {
  padding-left: 26px;
}

.settings__actions {
  margin-top: var(--spacing-md);
}

.form-group { margin-bottom: var(--spacing-sm); }

.form-label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--colorTextSecondary);
  margin-bottom: var(--spacing-xs);
}

.form-select {
  width: 100%;
  padding: var(--spacing-sm) 12px;
  font-size: var(--font-size-md);
  font-family: var(--font-family);
  color: var(--colorText);
  background-color: var(--colorBackground);
  border: 1px solid var(--colorBorder);
  border-radius: var(--radius-sm);
  transition: border-color var(--transition), box-shadow var(--transition);

  &:focus {
    outline: none;
    border-color: var(--colorBorderFocus);
    box-shadow: 0 0 0 1px var(--colorBorderFocus);
  }
}
</style>
